import type { ReactNode } from 'react';

import { Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { cn } from '@opendoor/atoms/utils';
import { IconLink, IconShare2 } from '@tabler/icons-react';

import { PdpFavoriteButton } from './PdpFavoriteButton';

import similarCardStyles from './PdpSimilarListingCard.module.css';

const APPEARANCES = {
  /** One white pill, as on the home view's hero. */
  solid: {
    group: 'items-center rounded-full bg-od-bg-primary/90 p-1',
    button: 'size-10 rounded-full border-0 bg-transparent p-0 shadow-none',
    icon: 24,
  },
  /** Separate frosted circles that match a listing card's photo arrows. */
  frosted: {
    group: 'items-center gap-2',
    button: cn('size-7 rounded-full p-0', similarCardStyles.glassButton),
    icon: 16,
  },
};

const LINK_ACTIONS = {
  share: { label: 'Share home', Icon: IconShare2 },
  // The property page's link button uses this icon and label, so both copy buttons match.
  copy: { label: 'Copy home link', Icon: IconLink },
};

type ListingPhotoActionsProps = {
  appearance: keyof typeof APPEARANCES;
  /** What the first button offers; `onLinkAction` runs it. Defaults to `share`. */
  linkAction?: keyof typeof LINK_ACTIONS;
  linkAnalyticsName: string;
  favoriteAnalyticsName: string;
  className?: string;
  isLiked: boolean;
  isFavoriteLoading: boolean;
  onLinkAction?: () => void;
  onToggleFavorite: () => void;
  /** Actions after the link and favorite buttons, such as closing the view. */
  children?: ReactNode;
};

/** Share (or copy link) and favorite actions over a listing's photo. */
export const ListingPhotoActions = ({
  appearance,
  linkAction = 'share',
  linkAnalyticsName,
  favoriteAnalyticsName,
  className,
  isLiked,
  isFavoriteLoading,
  onLinkAction,
  onToggleFavorite,
  children,
}: ListingPhotoActionsProps) => {
  const { group, button, icon } = APPEARANCES[appearance];
  const { label, Icon } = LINK_ACTIONS[linkAction];
  return (
    <Row className={cn(group, className)}>
      <Button
        variant="secondary"
        size="sm"
        className={button}
        aria-label={label}
        analyticsName={linkAnalyticsName}
        onClick={onLinkAction}
      >
        <Icon aria-hidden size={icon} stroke={2} />
      </Button>
      <PdpFavoriteButton
        size="sm"
        className={button}
        iconSize={icon}
        analyticsName={favoriteAnalyticsName}
        isLiked={isLiked}
        isFavoriteLoading={isFavoriteLoading}
        onToggleFavorite={onToggleFavorite}
      />
      {children}
    </Row>
  );
};
